import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type CreateOrganisationTaxRateRequest,
  type OrganisationTaxRate,
  type SeedOrganisationTaxRatesRequest,
  type UpdateOrganisationTaxRateRequest,
} from '../client';
import { organisationVatTreatmentKeys } from './useOrganisationVatTreatments';

/** Stable query-key factory for tax-rate-scoped caches. */
export const organisationTaxRateKeys = {
  all: ['directory', 'organisation-tax-rate'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-tax-rate', 'org', organisationId] as const,
};

/** Fetch every tax rate for an organisation. Pass `null` to keep disabled. */
export function useOrganisationTaxRates(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<OrganisationTaxRate[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrganisationTaxRate[], Error>({
    queryKey: organisationId
      ? organisationTaxRateKeys.byOrganisation(organisationId)
      : organisationTaxRateKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationTaxRates(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Mutation wrapper around POST `/organisationTaxRates`. */
export function useCreateOrganisationTaxRate() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateOrganisationTaxRateRequest>({
    mutationKey: [...organisationTaxRateKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createOrganisationTaxRate(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: organisationTaxRateKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/organisationTaxRates`. The caller passes
 * `organisationId` alongside the body so the per-org list-cache key is
 * invalidated correctly (the wire body itself only carries `taxRateId`).
 */
export function useUpdateOrganisationTaxRate() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; body: UpdateOrganisationTaxRateRequest }
  >({
    mutationKey: [...organisationTaxRateKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateOrganisationTaxRate(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationTaxRateKeys.byOrganisation(organisationId),
      });
    },
  });
}

/** Mutation wrapper around DELETE `/organisationTaxRates/{id}`. */
export function useDeleteOrganisationTaxRate() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...organisationTaxRateKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteOrganisationTaxRate(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationTaxRateKeys.byOrganisation(organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around POST `/organisationTaxRates/seed-defaults`.
 * Result: the count of newly created rows. Re-seeding is safe — rows
 * already created from a given seed (matched by SeedId) are skipped.
 */
export function useSeedOrganisationTaxRates() {
  const qc = useQueryClient();
  return useMutation<number, Error, SeedOrganisationTaxRatesRequest>({
    mutationKey: [...organisationTaxRateKeys.all, 'seed-defaults'],
    mutationFn: (body) => directoryApi.seedOrganisationTaxRates(body),
    onSuccess: (_count, vars) => {
      qc.invalidateQueries({
        queryKey: organisationTaxRateKeys.byOrganisation(vars.organisationId),
      });
      // Seeding also fills the VAT treatment matrix on the server.
      qc.invalidateQueries({
        queryKey: organisationVatTreatmentKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}
